/**
 * Excel/CSV Importer with AI column-mapping.
 *
 * Three-step wizard:
 *  1. Upload + pick entity (customers / products / suppliers).
 *  2. Review the AI-suggested mapping; tweak per-column.
 *  3. Download the converted canonical CSV (ready to feed the existing
 *     /customers/import-csv style importer).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Upload, Sparkles, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";

const ENTITIES = [
  { value: "customers", label: "Clientes" },
  { value: "products",  label: "Productos" },
  { value: "suppliers", label: "Proveedores" },
];

export default function ImportExcel() {
  const [step, setStep] = useState(1);
  const [entity, setEntity] = useState("customers");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);  // server response from /analyze
  const [mapping, setMapping] = useState({});      // sourceHeader -> canonicalKey

  const reset = () => {
    setStep(1); setFile(null); setAnalysis(null); setMapping({}); setBusy(false);
  };

  const analyze = async () => {
    if (!file) {
      toast.error("Subí un archivo primero");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("entity", entity);
      fd.append("file", file);
      const { data } = await api.post("/import/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAnalysis(data);
      setMapping(data.ai_mapping || {});
      setStep(2);
      toast.success(`IA mapeó ${Object.values(data.ai_mapping || {}).filter(Boolean).length} de ${data.headers.length} columnas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo analizar el archivo");
    } finally {
      setBusy(false);
    }
  };

  const convert = async () => {
    setBusy(true);
    try {
      const resp = await api.post(
        "/import/convert",
        { token: analysis.token, mapping },
        { responseType: "blob" },
      );
      // Trigger download
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = resp.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m?.[1] || `minegocio_${entity}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      const written = resp.headers["x-rows-written"];
      const skipped = resp.headers["x-rows-skipped"];
      toast.success(`CSV listo. ${written} filas convertidas${skipped > 0 ? ` (${skipped} omitidas por estar vacías)` : ""}`);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo convertir");
    } finally {
      setBusy(false);
    }
  };

  const mappedCount = Object.values(mapping).filter(Boolean).length;
  const requiredKeys = (analysis?.canonical || []).filter(f => f.required).map(f => f.key);
  const missingRequired = requiredKeys.filter(rk => !Object.values(mapping).includes(rk));

  return (
    <div className="space-y-6 pb-12" data-testid="import-excel-page">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/" data-testid="import-back"><ArrowLeft className="h-4 w-4 mr-1" /> Volver</Link>
        </Button>
        <h1 className="text-3xl sm:text-4xl font-black mt-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Convertidor de planillas
        </h1>
        <p className="text-slate-600 mt-1">
          Subí tu Excel o CSV en cualquier formato. La IA mapea automáticamente las columnas al formato de MiNegocio.
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-xs">
        {[
          { n: 1, label: "Subir archivo" },
          { n: 2, label: "Revisar mapeo" },
          { n: 3, label: "Descargar" },
        ].map((s, idx) => (
          <div key={s.n} className="flex items-center gap-2">
            <span
              className={`w-7 h-7 rounded-full grid place-items-center font-bold ${
                step >= s.n ? "bg-[#0B1F3A] text-white" : "bg-slate-200 text-slate-500"
              }`}
              data-testid={`import-step-${s.n}`}
            >
              {step > s.n ? <CheckCircle2 className="h-4 w-4" /> : s.n}
            </span>
            <span className={step === s.n ? "font-semibold text-[#0B1F3A]" : "text-slate-500"}>{s.label}</span>
            {idx < 2 && <span className="w-6 h-px bg-slate-200 ml-1" />}
          </div>
        ))}
      </div>

      {/* Step 1 — upload */}
      {step === 1 && (
        <Card className="p-6 bg-white border border-slate-200 rounded-sm space-y-4" data-testid="import-step-1-card">
          <div>
            <label className="block text-sm font-semibold mb-1.5">¿Qué vas a importar?</label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="rounded-sm" data-testid="import-entity-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITIES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5">Archivo (.xlsx, .csv)</label>
            <Input
              type="file"
              accept=".xlsx,.xlsm,.csv,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="import-file-input"
              className="rounded-sm cursor-pointer"
            />
            {file && (
              <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5">
                <FileSpreadsheet className="h-3.5 w-3.5" />
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
          <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900">
            <strong>Tip:</strong> tu archivo puede tener los nombres de columnas en cualquier idioma o
            formato (ej. "Nombre del cliente", "Customer Name", "Razón social"). La IA los reconoce.
          </div>
          <Button
            onClick={analyze}
            disabled={!file || busy}
            className="bg-[#0B1F3A] hover:bg-[#152C57] text-white"
            data-testid="import-analyze-button"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Analizar con IA
          </Button>
        </Card>
      )}

      {/* Step 2 — mapping */}
      {step === 2 && analysis && (
        <Card className="p-6 bg-white border border-slate-200 rounded-sm space-y-4" data-testid="import-step-2-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-sm text-slate-500">Archivo: <span className="font-semibold text-slate-900">{analysis.filename}</span> · {analysis.total_rows} filas</div>
              <div className="text-xs text-emerald-700 flex items-center gap-1 mt-1">
                <Sparkles className="h-3 w-3" />
                {mappedCount} de {analysis.headers.length} columnas mapeadas automáticamente
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={reset} data-testid="import-restart">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Subir otro
            </Button>
          </div>

          {missingRequired.length > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 text-sm flex gap-2" data-testid="import-missing-required">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                Faltan mapear campos obligatorios: <strong>{missingRequired.map(k => analysis.canonical.find(f => f.key === k)?.label || k).join(", ")}</strong>
              </div>
            </div>
          )}

          <div className="overflow-x-auto border border-slate-200 rounded-sm">
            <table className="w-full text-sm" data-testid="import-mapping-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-semibold">Columna de tu archivo</th>
                  <th className="text-left py-2 px-3 font-semibold">Ejemplo</th>
                  <th className="text-left py-2 px-3 font-semibold">→ Campo en MiNegocio</th>
                </tr>
              </thead>
              <tbody>
                {analysis.headers.map((h, idx) => {
                  const sampleValue = analysis.sample[0]?.[idx] || analysis.sample[1]?.[idx] || "";
                  return (
                    <tr key={h + idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 font-medium">{h || <em className="text-slate-400">(sin nombre)</em>}</td>
                      <td className="py-2 px-3 text-slate-500 max-w-[220px] truncate" title={sampleValue}>{sampleValue || "—"}</td>
                      <td className="py-2 px-3">
                        <Select
                          value={mapping[h] || "__none__"}
                          onValueChange={(v) => setMapping(m => ({ ...m, [h]: v === "__none__" ? "" : v }))}
                        >
                          <SelectTrigger className="rounded-sm h-8 text-xs w-full max-w-[280px]" data-testid={`import-map-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— No importar —</SelectItem>
                            {analysis.canonical.map(f => (
                              <SelectItem key={f.key} value={f.key}>
                                {f.label}{f.required ? " *" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center pt-2">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="import-back-step1">← Atrás</Button>
            <Button
              onClick={convert}
              disabled={busy || missingRequired.length > 0}
              className="bg-[#0B1F3A] hover:bg-[#152C57] text-white"
              data-testid="import-convert-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Generar CSV en formato MiNegocio
            </Button>
          </div>
        </Card>
      )}

      {/* Step 3 — success */}
      {step === 3 && (
        <Card className="p-8 bg-white border border-slate-200 rounded-sm text-center space-y-4" data-testid="import-step-3-card">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mx-auto">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-black" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            ¡Listo! Tu archivo está descargado.
          </h2>
          <p className="text-slate-600 max-w-md mx-auto">
            Ahora podés importarlo desde la sección de {ENTITIES.find(e => e.value === entity)?.label} usando el botón <strong>Importar CSV</strong>.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button variant="outline" onClick={reset} data-testid="import-do-another">
              <Upload className="h-4 w-4 mr-2" />Subir otro archivo
            </Button>
            <Button asChild className="bg-[#0B1F3A] hover:bg-[#152C57] text-white">
              <Link to={`/${entity === "customers" ? "customers" : entity === "products" ? "products" : "suppliers"}`} data-testid="import-go-to-section">
                Ir a {ENTITIES.find(e => e.value === entity)?.label}
              </Link>
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
